import React, { useEffect, useState } from 'react';
import { supabase, supabaseUrl } from '../supabaseClient';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
} from 'recharts';
import './pages.css';

export default function AdminDashboard() {
  const [activeUsers, setActiveUsers] = useState(0);
  const [events, setEvents] = useState([]);
  const [sales, setSales] = useState(0);
  const [eventsCount, setEventsCount] = useState(0);
  const [showCreateEvent, setShowCreateEvent] = useState(false);
  const [editingEventId, setEditingEventId] = useState(null);
  const [salesSummary, setSalesSummary] = useState([]);
  const [showSalesPopup, setShowSalesPopup] = useState(false);

  const [tittle, setTittle] = useState('');
  const [description, setDescription] = useState('');
  const [date, setDate] = useState('');
  const [location, setLocation] = useState('');
  const [category, setCategory] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [message, setMessage] = useState('');
  const [price, setPrice] = useState(0);
  const [time, setTime] = useState('');
  const [chartData, setChartData] = useState([]);

  const fetchSalesData = async () => {
    const { data: salesData, error: salesError } = await supabase
      .from('ticket_sales')
      .select('event_id, amount');

    const { data: eventsData, error: eventError } = await supabase
      .from('events')
      .select('id, tittle');

    if (salesError || eventError) {
      console.error("Error loading data");
      return;
    }

    const summaryMap = {};

    for (let sale of salesData) {
      const event = eventsData.find(e => e.id === sale.event_id);
      const title = event?.tittle || 'Unknown';
      if (!summaryMap[title]) {
        summaryMap[title] = { title, bookings: 0, revenue: 0 };
      }
      summaryMap[title].bookings += 1;
      summaryMap[title].revenue += sale.amount;
    }

    const summaryList = Object.values(summaryMap);
    setChartData(summaryList);
  };

  const fetchSalesSummary = async () => {
    const { data: salesData, error: salesError } = await supabase
      .from('ticket_sales')
      .select('event_id, amount');

    const { data: eventData, error: eventError } = await supabase
      .from('events')
      .select('id, tittle, price');

    if (salesError || eventError) {
      console.error("Error fetching sales insights");
      return;
    }

    const summaryMap = {};

    for (let sale of salesData) {
      const id = sale.event_id;
      if (!summaryMap[id]) {
        const ev = eventData.find(e => e.id === id);
        summaryMap[id] = {
          title: ev?.tittle || "Unknown Event",
          price: ev?.price || 0,
          count: 0,
          revenue: 0,
        };
      }
      summaryMap[id].count += 1;
      summaryMap[id].revenue += sale.amount;
    }

    setSalesSummary(Object.values(summaryMap));
  };

  useEffect(() => {
    fetchSalesData();
    fetchStats();
    const fetchEvents = async () => {
      const { data, error } = await supabase.from('events').select('*');
      if (!error) setEvents(data);
    };
    fetchEvents();
  }, []);

  const fetchStats = async () => {
    const { count: userCount } = await supabase
      .from('users')
      .select('*', { count: 'exact', head: true });
    setActiveUsers(userCount ?? 0);

    const { count: eventCount } = await supabase
      .from('events')
      .select('*', { count: 'exact', head: true });
    setEventsCount(eventCount ?? 0);

    const { data: salesData, error: salesError } = await supabase
      .from('ticket_sales')
      .select('amount');

    if (salesError) {
      console.error('❌ Error fetching sales:', salesError.message);
      setSales(0);
    } else {
      const totalSales = salesData?.reduce((acc, sale) => acc + (sale.amount || 0), 0) ?? 0;
      setSales(totalSales);
    }
  };

  const startEdit = (event) => {
    setEditingEventId(event.id);
    setTittle(event.tittle);
    setDescription(event.description);
    setDate(event.date);
    setTime(event.time);
    setLocation(event.location);
    setCategory(event.category);
    setPrice(event.price);
    setImageFile(null);
    setShowCreateEvent(true);
    setMessage('');
  };

  const cancelEdit = () => {
    setEditingEventId(null);
    setTittle('');
    setDescription('');
    setDate('');
    setTime('');
    setLocation('');
    setCategory('');
    setPrice(0);
    setImageFile(null);
    setMessage('');
    setShowCreateEvent(false);
  };

  const deleteEvent = async (id) => {
    const confirm = window.confirm("Are you sure you want to delete this event?");
    if (!confirm) return;

    const { error } = await supabase.from('events').delete().eq('id', id);
    if (error) {
      alert("Failed to delete event");
    } else {
      setEvents(events.filter(e => e.id !== id));
    }
    window.location.reload();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    let imageUrl = '';

    if (imageFile) {
      const fileExt = imageFile.name.split('.').pop();
      const fileName = `${Date.now()}.${fileExt}`;
      const { error: uploadError } = await supabase.storage
        .from('event-images')
        .upload(fileName, imageFile);

      if (uploadError) {
        setMessage('Image upload failed');
        return;
      }

      imageUrl = `${supabaseUrl}/storage/v1/object/public/event-images/${fileName}`;
    }

    const payload = {
      tittle,
      description,
      date,
      time,
      location,
      category,
      price: parseFloat(price),
    };

    if (imageUrl) {
      payload.image_url = imageUrl;
    } else if (editingEventId) {
      const existingEvent = events.find(e => e.id === editingEventId);
      if (existingEvent?.image_url) {
        payload.image_url = existingEvent.image_url;
      }
    }

    if (editingEventId) {
      const { error } = await supabase
        .from('events')
        .update(payload)
        .eq('id', editingEventId);

      if (error) {
        setMessage(`Error updating event: ${error.message}`);
      } else {
        setMessage('✅ Event updated!');
        setEditingEventId(null);
        window.location.reload();
      }
    } else {
      const { error } = await supabase.from('events').insert(payload);
      if (error) {
        setMessage(`Error creating event: ${error.message}`);
      } else {
        setMessage('✅ Event created!');
        window.location.reload();
      }
    }
  };

  return (
    <div className="admin-dashboard" style={{ width: '100%', textAlign: 'center' }}>
      <h2>Admin Dashboard</h2>

      <div className="dashboard-cards">
        <div className="card"><h3>Active Users (30d)</h3><p>{activeUsers}</p></div>
        <div className="card"><h3>Total Events</h3><p>{eventsCount}</p></div>
        <div className="card">
          <h3>Total Sales</h3>
          <p>Ksh.{sales}</p>
          <button className="admin-btn small" onClick={() => {
            fetchSalesSummary();
            setShowSalesPopup(true);
          }}>📊 View Sales Insights</button>
        </div>
      </div>

      {showSalesPopup && (
        <div className="popup-overlay">
          <div className="popup-content">
            <h3>📊 Sales Insights</h3>
            <div className="chart-container">
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="title" interval={0} angle={-45} textAnchor="end" height={100} />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="bookings" fill="#8884d8" name="Bookings" />
                  <Bar dataKey="revenue" fill="#82ca9d" name="Revenue (Ksh)" />
                </BarChart>
              </ResponsiveContainer>
            </div>
            {salesSummary.length === 0 ? (
              <p>No ticket sales yet.</p>
            ) : (
              <table className="sales-table">
                <thead>
                  <tr>
                    <th>Event</th>
                    <th>Price</th>
                    <th>Bookings</th>
                    <th>Total Revenue (Ksh)</th>
                  </tr>
                </thead>
                <tbody>
                  {salesSummary.map((s, idx) => (
                    <tr key={idx}>
                      <td>{s.title}</td>
                      <td>Ksh. {s.price.toFixed(2)}</td>
                      <td>{s.count}</td>
                      <td>Ksh. {s.revenue.toFixed(2)}</td>
                    </tr>
                  ))}
                  <tr className="total-summary-row">
                    <td colSpan="2"><strong>Total Sales</strong></td>
                    <td><strong>{salesSummary.reduce((acc, s) => acc + s.count, 0)}</strong></td>
                    <td><strong>Ksh.{sales.toFixed(2)}</strong></td>
                  </tr>
                </tbody>
              </table>
            )}
            <button className="admin-btn cancel" onClick={() => setShowSalesPopup(false)}>Close</button>
          </div>
        </div>
      )}

      {/* Create / Edit Event */}
      <div className="dashboard-actions">
        <button className="admin-btn" onClick={() => setShowCreateEvent(!showCreateEvent)}>
          {showCreateEvent ? '− Hide Create Event' : '+ Create Event'}
        </button>

        {showCreateEvent && (
          <div className="create-event-form fade-in">
            <form onSubmit={handleSubmit} className="event-form">
              <div className="form-group">
                <label>Event Title</label>
                <input type="text" value={tittle} onChange={(e) => setTittle(e.target.value)} required />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea value={description} onChange={(e) => setDescription(e.target.value)} required />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Date</label>
                  <input type="date" value={date} onChange={(e) => setDate(e.target.value)} required />
                </div>
                <div className="form-group">
                  <label>Time</label>
                  <input type="time" value={time} onChange={(e) => setTime(e.target.value)} required />
                </div>
              </div>
              <div className="form-group">
                <label>Location</label>
                <input type="text" value={location} onChange={(e) => setLocation(e.target.value)} required />
              </div>
              <div className="form-group">
                <label>Category</label>
                <input type="text" value={category} onChange={(e) => setCategory(e.target.value)} required />
              </div>
              <div className="form-group">
                <label>Price</label>
                <input type="number" value={price} onChange={(e) => setPrice(e.target.value)} required />
              </div>
              <div className="form-group">
                <label>Event Image</label>
                <input type="file" accept="image/*" onChange={(e) => setImageFile(e.target.files[0])} />
              </div>
              <button type="submit" className="admin-btn">
                {editingEventId ? 'Update Event' : 'Create Event'}
              </button>
              {editingEventId && (
                <button type="button" className="admin-btn cancel" onClick={cancelEdit}>
                  Cancel Edit
                </button>
              )}
              {message && <p className="message">{message}</p>}
            </form>
          </div>
        )}
      </div>

      {/* Events List */}
      <div className="admin-events">
        <h3>Manage Events</h3>
        <table className="event-table">
          <thead>
            <tr>
              <th>Title</th>
              <th>Date</th>
              <th>Location</th>
              <th>Category</th>
              <th>Time</th>
              <th>Price</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {Array.isArray(events) && events.length > 0 ? (
              events.map(ev => (
                <tr key={ev.id}>
                  <td>{ev.tittle}</td>
                  <td>{ev.date}</td>
                  <td>{ev.location}</td>
                  <td>{ev.category}</td>
                  <td>{ev.time}</td>
                  <td>Ksh.{ev.price.toFixed(2)}</td>
                  <td>
                    <button className="admin-btn small" onClick={() => startEdit(ev)}>Edit</button>
                    <button className="admin-btn small danger" onClick={() => deleteEvent(ev.id)}>Delete</button>
                  </td>
                </tr>
              ))
            ) : (
              <tr><td colSpan="7">No events found.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
